import { forwardRef, useState } from "react";

import Input, { type InputProps } from "./Input";

type TextInputProps = Omit<InputProps<string>, "type" | "parse">;

interface TextInputState {
  error: string | undefined;
}

const DEFAULT_STATE: TextInputState = {
  error: "",
};

const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      size,
      value,
      min,
      max,
      placeholder,
      label,
      labelTooltip,
      leading,
      trailing,
      error,
      isRequired,
      isDisabled,
      autoFocus,
      onChange,
      onKeyDown,
      onBlur,
      fillWidth,
      width,
      isMonospace,
    },
    ref,
  ) => {
    const [state, setState] = useState<TextInputState>(DEFAULT_STATE);

    const handleFocus = () => {
      setState({
        ...state,
        error: undefined,
      });
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (value?.length && min && value.length < min) {
        setState({
          ...state,
          error: `Must be at least ${min} characters`,
        });
      }

      if (value?.length && max && value.length > max) {
        setState({
          ...state,
          error: `Must be less than ${max} characters`,
        });
      }

      onBlur?.(e);
    };

    return (
      <Input
        parse={String}
        onChange={onChange}
        size={size}
        ref={ref}
        type="text"
        value={value}
        min={min}
        max={max}
        placeholder={placeholder}
        label={label}
        labelTooltip={labelTooltip}
        leading={leading}
        trailing={trailing}
        autoFocus={autoFocus}
        error={state.error || error}
        isRequired={isRequired}
        isDisabled={isDisabled}
        onKeyDown={onKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        fillWidth={fillWidth}
        width={width}
        isMonospace={isMonospace}
      />
    );
  },
);

TextInput.displayName = "TextInput";

export default TextInput;
